import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import authService from '../../api/services/authService';
import { handleApiError } from '../../utils/helpers';

const VerifyResetOtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { success, error } = useToast();
  const { dark } = useTheme();
  const d = dark;

  const phone = location.state?.phone || '';
  const [otp, setOtp] = useState(['','','','','','']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef([]);

  useEffect(() => { if (!phone) navigate('/forgot-password', { replace: true }); }, [phone, navigate]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (pasted.length === 6) { setOtp(pasted.split('')); inputRefs.current[5]?.focus(); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== 6) { error('Please enter the full 6-digit OTP'); return; }
    setLoading(true);
    try {
      const response = await authService.verifyResetOtp(phone, otpValue);
      if (response.success) { success('OTP verified!'); navigate('/reset-password', { state: { phone } }); }
    } catch (err) {
      error(handleApiError(err));
      setOtp(['','','','','','']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await authService.forgotPassword(phone);
      success('A new OTP has been sent to your phone.');
      setCountdown(60);
      setOtp(['','','','','','']);
      inputRefs.current[0]?.focus();
    } catch (err) { error(handleApiError(err)); }
    finally { setResending(false); }
  };

  const maskedPhone = phone ? phone.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2') : '';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        .lp * { font-family: 'Plus Jakarta Sans', sans-serif !important; }
        @keyframes lp-up   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lp-left { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
        @keyframes lp-blob {
          0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; transform:scale(1) translate(0,0); }
          33%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; transform:scale(1.08) translate(10px,-10px); }
          66%      { border-radius:50% 60% 30% 60%/40% 30% 60% 50%; transform:scale(.95) translate(-8px,8px); }
        }
        @keyframes lp-grad  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes lp-shine { 0%{transform:translateX(-100%) skewX(-15deg)} 100%{transform:translateX(250%) skewX(-15deg)} }
        .lp .au { animation:lp-up   .7s cubic-bezier(.22,1,.36,1) both; }
        .lp .al { animation:lp-left .7s cubic-bezier(.22,1,.36,1) both; }
        .lp .d1{animation-delay:.08s} .lp .d2{animation-delay:.16s} .lp .d3{animation-delay:.24s} .lp .d4{animation-delay:.32s}
        .lp .blob{animation:lp-blob 9s ease-in-out infinite;position:absolute;filter:blur(56px);}
        .lp .blob-2{animation-delay:-3s;} .lp .blob-3{animation-delay:-6s;}
        .lp .grad-text { background:linear-gradient(135deg,#ef4444 0%,#f97316 40%,#ef4444 70%,#dc2626 100%); background-size:300% 300%; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:lp-grad 4s ease infinite; }
        .lp .btn-submit { background:linear-gradient(135deg,#ef4444,#dc2626,#b91c1c); background-size:200% 200%; animation:lp-grad 3s ease infinite; transition:transform .2s,box-shadow .2s; position:relative; overflow:hidden; }
        .lp .btn-submit:hover { transform:translateY(-2px) scale(1.02); box-shadow:0 16px 40px rgba(239,68,68,.45); }
        .lp .btn-submit:active{transform:scale(.97);}
        .lp .btn-submit::after { content:''; position:absolute; top:0; left:0; width:35%; height:100%; background:linear-gradient(90deg,transparent,rgba(255,255,255,.15),transparent); transform:translateX(-100%) skewX(-15deg); }
        .lp .btn-submit:hover::after{animation:lp-shine .5s ease forwards;}
        .lp .otp-input { width:52px; height:60px; font-size:24px; font-weight:900; text-align:center; border-radius:14px; border:2px solid; outline:none; transition:all .2s; }
        .lp.dark-mode .otp-input  { background:rgba(255,255,255,.05); border-color:rgba(255,255,255,.12); color:white; }
        .lp.dark-mode .otp-input:focus { border-color:#ef4444; box-shadow:0 0 0 3px rgba(239,68,68,.2); background:rgba(239,68,68,.08); }
        .lp.dark-mode .otp-input:not(:placeholder-shown) { border-color:rgba(239,68,68,.5); }
        .lp.light-mode .otp-input { background:white; border-color:#e5e7eb; color:#111827; }
        .lp.light-mode .otp-input:focus { border-color:#ef4444; box-shadow:0 0 0 3px rgba(239,68,68,.1); }
        .lp.light-mode .otp-input:not(:placeholder-shown) { border-color:#fca5a5; }
        .lp .grid-bg-dark { background-image:linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px); background-size:48px 48px; }
        .lp .grid-bg-light { background-image:linear-gradient(rgba(0,0,0,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.04) 1px,transparent 1px); background-size:48px 48px; }
      `}</style>

      <Navbar />

      <div className={`lp ${d ? 'dark-mode' : 'light-mode'} min-h-screen flex pt-20 transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-gray-50'}`}>

        {/* LEFT PANEL */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <div className="absolute inset-0" style={{ background: d ? 'linear-gradient(135deg,#030712 0%,#1a0000 40%,#030712 100%)' : 'linear-gradient(135deg,#fff1f2 0%,#ffe4e6 40%,#fff1f2 100%)' }}/>
          <div className={`absolute inset-0 ${d ? 'grid-bg-dark' : 'grid-bg-light'}`}/>
          <div className="blob blob-1 w-80 h-80 top-16 left-10 opacity-30" style={{ background: d?'#ef4444':'#fca5a5' }}/>
          <div className="blob blob-2 w-72 h-72 top-48 right-8 opacity-20"  style={{ background: d?'#fff':'#fed7aa' }}/>
          <div className="blob blob-3 w-64 h-64 bottom-20 left-32 opacity-25" style={{ background: d?'#dc2626':'#fca5a5' }}/>
          <div className="relative z-10 flex flex-col justify-center px-14 py-16">
            <div className="al d1 mb-3">
              <span className={`text-xs font-black tracking-widest uppercase px-3 py-1 rounded-full border ${d ? 'text-red-400 bg-red-500/10 border-red-500/20' : 'text-red-600 bg-red-50 border-red-200'}`}>Verification</span>
            </div>
            <h1 className={`al d2 text-5xl font-black leading-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Check your<br/><span className="grad-text">phone</span>
            </h1>
            <p className={`al d3 text-base mb-12 leading-relaxed max-w-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              We've sent a 6-digit code to <strong className={d ? 'text-white' : 'text-gray-800'}>{maskedPhone}</strong>. Enter it to continue.
            </p>
            <div className={`al d4 rounded-2xl p-5 border ${d ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-md'}`}>
              <p className={`text-sm font-bold mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>💡 Tips</p>
              <ul className={`text-xs space-y-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                <li>• The code expires in <strong>15 minutes</strong></li>
                <li>• Check your SMS inbox</li>
                <li>• You can paste the code directly</li>
                <li>• Request a new code if it doesn't arrive</li>
              </ul>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className={`w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 lg:p-14 relative ${d ? 'bg-gray-950' : 'bg-white'}`}>
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none" style={{ background:'radial-gradient(circle,#ef4444,transparent)' }}/>
          <div className="w-full max-w-md relative z-10">

            {/* Step indicator */}
            <div className="au d1 flex items-center gap-2 justify-center mb-6">
              {[1,2,3].map(n => (
                <div key={n} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border-2 ${n <= 2 ? 'bg-red-500 border-red-500 text-white' : d ? 'border-white/20 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                    {n === 1 ? '✓' : n}
                  </div>
                  {n < 3 && <div className={`w-8 h-0.5 ${n === 1 ? 'bg-red-500' : d ? 'bg-white/10' : 'bg-gray-200'}`}/>}
                </div>
              ))}
            </div>

            <div className="au d1 text-center mb-8">
              <h2 className={`text-3xl font-black mb-1.5 ${d ? 'text-white' : 'text-gray-900'}`}>Enter OTP</h2>
              <p className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>Step 2 of 3 — Verify your identity</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className={`au d2 rounded-2xl p-6 border transition-all duration-300 ${d ? 'bg-gray-900/80 border-white/8 hover:border-red-500/30' : 'bg-gray-50 border-gray-200 hover:border-red-300 shadow-sm'}`}>
                <p className={`text-xs font-semibold mb-4 text-center ${d ? 'text-gray-400' : 'text-gray-500'}`}>Code sent to {maskedPhone}</p>
                <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                  {otp.map((digit, i) => (
                    <input key={i} ref={el => inputRefs.current[i] = el}
                      type="text" inputMode="numeric" maxLength={1} value={digit} placeholder="·"
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      className="otp-input" autoFocus={i === 0} />
                  ))}
                </div>
              </div>

              <button type="submit" disabled={loading || otp.join('').length !== 6}
                className="au d3 btn-submit w-full text-white font-black text-base py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                {loading ? (
                  <><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>Verifying...</>
                ) : (
                  <>Verify OTP<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg></>
                )}
              </button>

              <div className="au d4 text-center">
                {countdown > 0 ? (
                  <p className={`text-sm ${d ? 'text-gray-500' : 'text-gray-400'}`}>Resend code in <span className="font-bold text-red-500">{countdown}s</span></p>
                ) : (
                  <button type="button" onClick={handleResend} disabled={resending}
                    className="text-sm text-red-500 hover:text-red-400 font-bold transition-colors disabled:opacity-50">
                    {resending ? 'Sending...' : 'Resend OTP'}
                  </button>
                )}
                <p className={`mt-2 text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  Wrong number?{' '}
                  <Link to="/forgot-password" className="text-red-500 hover:text-red-400 font-bold transition-colors">Go back</Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyResetOtpPage;